import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import utils from "@utils";
import { Link } from "react-router";
import { JOB_APPROVAL_TYPES_MAP, SUPPERVISOR_APPROVAL_MENU } from "@constants";
import { JurisdictionJobApplicationsService } from "@services";
import { HeaderGrid, Image, ScrollDetect, TabMenu } from "../../../components";
import "./JobApprovalList.scss";

let cancel;

class JobApprovalList extends Component {
	constructor(props) {
		super(props);
		this.state = {
			pageNumber: 1,
			search: "",
			type: 30,
			jobApprovals: [],
			hasMore: true,
			jobCounts: {
				Pending: "",
				Approved: "",
				Denied: "",
			},
			loadingCounts: false,
			selectedJobs: [],
		};
		if (
			!props.user.Organization.ExtraDutyApprovalRequired ||
			props.user.IsInternalCoordinator
		) {
			window.location.href = "/";
		}
	}

	componentDidMount() {
		const { type } = this.state;
		this.setState({ loadingCounts: true });
		this.getJobCounts();
		this.setActiveType(type);
	}

	/** Build and update a URL to reflect the page's current filters */
	getUrl = () => {
		const { type } = this.state;
		const { router } = this.props;
		let activeViewType;
		switch (type) {
			case 10:
				activeViewType = "approved";
				break;
			case 20:
				activeViewType = "denied";
				break;
			default:
				activeViewType = "pending";
				break;
		}
		router.push(`/admin/approvals/list/${activeViewType}`);
	};

	/**
	 * Changes the type of job approvals shown in the list. After updating approval type, {@link getUrl} is automatically called.
	 * @param {Number|'10'|'20'|'30'} type A value indicating the approval type to view
	 */
	setActiveType = type => {
		/* Job Approval Types
		 * 30 = Pending Approval
		 * 10 = Approved
		 * 20 = Denied
		 */
		const jobType = JOB_APPROVAL_TYPES_MAP.find(job => job.type === parseInt(type, 10));
		this.setState(
			{
				type: jobType?.type || 30,
				jobApprovals: [],
				hasMore: true,
				pageNumber: 1,
			},
			() => {
				this.getUrl();
			},
		);
	};

	/** Retrieves the number of jobs that are in each approval status */
	getJobCounts = () => {
		const { lang } = global;
		const { jobCounts } = this.state;
		JurisdictionJobApplicationsService.getCounts()
			.then(({ data }) => {
				this.setState({
					jobCounts: {
						...jobCounts,
						...data,
					},
				});
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: lang.t("Error loading jobs"),
						timeOut: 1500,
					});
				}
			})
			.finally(() => {
				const { loadingCounts } = this.state;
				if (loadingCounts) this.setState({ loadingCounts: false });
			});
	};

	/** Retrieves a paginated list of job approvals for the current user */
	getJobApprovals = () => {
		const { lang } = global;
		const { pageNumber, search, type } = this.state;
		if (cancel) {
			cancel();
		}
		JurisdictionJobApplicationsService.getAll({
			queryParams: {
				pageNumber,
				displayCount: 25,
				search,
				status: type,
			},
			getCancelToken: c => {
				cancel = c;
			},
		})
			.then(({ data }) => {
				cancel = undefined;
				this.setState(prevState => ({
					pageNumber: prevState.pageNumber + 1,
					jobApprovals: [...prevState.jobApprovals, ...data.ItemList],
					hasMore: data.ItemList.length >= 25,
				}));
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					this.setState({ pageNumber: 1, jobApprovals: [], hasMore: false });
					utils.notify({
						type: "error",
						message: lang.t("Error loading jobs"),
						timeOut: 1500,
					});
				}
			});
	};

	selectAllJobs = () => {
		const { jobApprovals, selectedJobs } = this.state;
		const selected = [];

		if (selectedJobs.length === jobApprovals.length) {
			this.setState({ selectedJobs: [] });
		} else {
			jobApprovals.map(item => selected.push(item.JobAssignmentId));
			this.setState({ selectedJobs: selected });
		}
	};

	/**
	 * Updates the selection of a job. If unchecked, it will now be checked and vice versa
	 * @param {Number} assignmentId The ID number for the job assignment to add or remove from the selected records
	 */
	updateSelection = (e, assignmentId) => {
		const { selectedJobs } = this.state;

		if (e.target.checked && !selectedJobs.includes(assignmentId)) {
			selectedJobs.push(assignmentId);
			this.setState({ selectedJobs });
		} else if (!e.target.checked && selectedJobs.includes(assignmentId)) {
			const unSelected = selectedJobs.filter(id => id !== assignmentId);
			this.setState({ selectedJobs: unSelected });
		}
	};

	/**
	 * Updates the approval value for the current list of selected jobs by calling {@link updateSelectedJobs}
	 * @param {Boolean} approvalType Whether the selected jobs should be approved or not
	 */
	updateJobs = approvalType => {
		const { selectedJobs } = this.state;

		if (selectedJobs.length > 0) {
			this.updateSelectedJobs(selectedJobs, approvalType);
		}
	};

	/**
	 * Updates the approval value for a single job by calling {@link updateSelectedJobs}
	 * @param {Number} assignmentId the ID number for the job assignment to approve or deny
	 * @param {Boolean} approvalType Whether the job should be approved or not
	 */
	updateSingleJob = (assignmentId, approvalType) => e => {
		e.stopPropagation();
		this.updateSelectedJobs([assignmentId], approvalType);
	};

	approveJob = (job, approval) => {
		const { lang } = global;
		JurisdictionJobApplicationsService.update({
			queryParams: {
				Approved: approval,
			},
			data: [job.JobAssignmentId],
		})
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Job application approved"),
					timeOut: 1500,
				});
				this.setState(
					prevState => ({
						jobApprovals: prevState.jobApprovals.filter(
							j => j.JobAssignmentId !== job.JobAssignmentId,
						),
					}),
					() => this.getJobCounts(),
				);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating job application"),
					timeOut: 1500,
				});
			});
	};

	denyJob = (job, approval) => {
		const { lang } = global;
		JurisdictionJobApplicationsService.update({
			queryParams: {
				Approved: approval,
			},
			data: [job.JobAssignmentId],
		})
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Job application denied"),
					timeOut: 1500,
				});
				this.setState(
					prevState => ({
						jobApprovals: prevState.jobApprovals.filter(
							j => j.JobAssignmentId !== job.JobAssignmentId,
						),
					}),
					() => this.getJobCounts(),
				);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating job application"),
					timeOut: 1500,
				});
			});
	};

	onClickTab = ({ tabNumber, url }) => {
		const {
			router: { push },
		} = this.props;
		const thisTab = SUPPERVISOR_APPROVAL_MENU.find(tab => tab.type === "approval");
		if (tabNumber !== thisTab.tabNumber) push(url);
	};

	/**
	 * Calls the API service to update a list of job approvals
	 * @param {Array} selectedJobs The list of jobs to update approval values for
	 * @param {Boolean} Approved Whether or not the list of jobs should be approved
	 */
	updateSelectedJobs = (selectedJobs, Approved) => {
		const { lang } = global;

		JurisdictionJobApplicationsService.update({
			queryParams: {
				Approved,
			},
			data: selectedJobs,
		})
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t(
						Approved ? "Job applications approved" : "Job applications denied",
					),
					timeOut: 1500,
				});
				this.setState(
					prevState => ({
						jobApprovals: prevState.jobApprovals.filter(
							j => selectedJobs.indexOf(j.JobAssignmentId) === -1,
						),
					}),
					() => this.getJobCounts(),
				);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating job applications"),
					timeOut: 1500,
				});
			});
	};

	render() {
		const { lang } = global;
		const { jobCounts, loadingCounts, hasMore, jobApprovals, selectedJobs, type } = this.state;
		const approvalTabs = SUPPERVISOR_APPROVAL_MENU;
		const { user } = this.props;
		return (
			<div
				id="jobapproval-list"
				className="container"
			>
				<TabMenu
					tabs={approvalTabs}
					activeTab={approvalTabs.find(tab => tab.type === "approval").tabNumber}
					onClickTab={this.onClickTab}
				/>
				{/* Header */}
				<div className="row page-title">
					<div className="col-xs-12">
						<h2>{lang.t("Supervisor Approvals")}</h2>
					</div>
				</div>
				{/* Header */}

				{/* Job Approval Grid */}
				<HeaderGrid
					gridOptions={JOB_APPROVAL_TYPES_MAP}
					loadingCounts={loadingCounts}
					counts={jobCounts}
					activeGrid={type}
					setActive={this.setActiveType}
				/>
				{/* Job Approval Grid */}

				<If condition={type === 30}>
					<div className="row visible-xs visible-sm">
						<div className="col-xs-12 checkbox">
							<label htmlFor="selectAllJobs">
								<input
									type="checkbox"
									name="selectAll"
									id="selectAll"
									checked={
										selectedJobs.length === jobApprovals.length &&
										selectedJobs.length !== 0
									}
									onChange={() => {
										this.selectAllJobs();
									}}
									title={lang.t("Select All")}
								/>
								{lang.t("Select All")}
							</label>
						</div>
					</div>
				</If>

				{/* Job Approval List */}
				<div className="list-header v-align row hidden-xs hidden-sm">
					<div className="col-md-2">
						<div className="row v-align">
							<div className={type === 30 ? "col-md-2 checkbox" : "hidden"}>
								<label htmlFor="selectAllJobs">
									<input
										type="checkbox"
										name="selectAll"
										id="selectAll"
										checked={
											selectedJobs.length === jobApprovals.length &&
											selectedJobs.length !== 0
										}
										onChange={() => {
											this.selectAllJobs();
										}}
										title={lang.t("Select All")}
									/>
								</label>
							</div>
							<div className={`${type === 30 ? "col-md-10" : "col-md-12"}`}>
								<strong>{lang.t("Location")}</strong>
							</div>
						</div>
					</div>
					<div className={type === 30 ? "col-md-2 col-lg-2" : "col-md-2"}>
						<strong>{lang.t("Personnel")}</strong>
					</div>
					<div className={type === 30 ? "col-md-2" : "col-md-2"}>
						<strong>{lang.t("Supervisor")}</strong>
					</div>
					<div className={type === 30 ? "col-md-2" : "col-md-2"}>
						<strong>{lang.t("Requested Date")}</strong>
					</div>
					<div className={type === 30 ? "col-md-2" : "col-md-2"}>
						<strong>{lang.t("Job Start Date")}</strong>
					</div>
					<div className={type === 30 ? "col-md-1" : "col-md-1"}>
						<strong>{lang.t("Status")}</strong>
					</div>
					<div className={type === 30 ? "col-md-1" : "col-md-1"}>
						<strong>{lang.t("Date")}</strong>
					</div>
				</div>
				<div className="list-body">
					<For
						each="job"
						of={jobApprovals}
					>
						<Link
							key={`job_${job.JobAssignmentId}`}
							to={`/jobs/${job.JobEventId}`}
						>
							<div className="row v-align-md">
								<div className="col-xs-12 col-md-2">
									<div className="row v-align">
										<div
											className={`col-xs-12 ${type === 30 ? "col-md-2 checkbox" : "hidden"}`}
										>
											<label htmlFor="selectJob">
												<input
													type="checkbox"
													name="selectJob"
													checked={selectedJobs.includes(
														job.JobAssignmentId,
													)}
													onClick={e => e.stopPropagation()}
													onChange={e =>
														this.updateSelection(e, job.JobAssignmentId)
													}
												/>
											</label>
										</div>
										<div
											className={`col-xs-12 ${type === 30 ? "col-md-10" : "col-md-12"}`}
										>
											<strong>{job.Location.Name}</strong>
											<div>{job.Location.StreetAddress}</div>
											<div>{`${job.Location.City}, ${job.Location.State} ${job.Location.Zipcode}`}</div>
										</div>
									</div>
									<br className="visible-xs visible-sm" />
								</div>

								<div
									className={`col-xs-12 ${type === 30 ? "col-md-2 col-lg-2" : "col-md-2"} flex flex-align-items-center`}
								>
									<Image
										className="img-circle"
										src={job.Officer.ContactPhotoUrl}
										fallbackSrc="avatar"
										alt="user-image"
									/>
									<span className="officer-name truncate">
										<div>
											<strong>{`${job.Officer.FirstName} ${job.Officer.LastName}`}</strong>
										</div>
										<div>
											<em>{`${job.Officer.OfficerRank}`}</em>
										</div>
									</span>
								</div>
								<div
									className={`col-xs-12 ${type === 30 ? "col-md-2 col-lg-2" : "col-md-2"} margin-md-top-15 flex flex-align-items-center`}
								>
									<Image
										className="img-circle"
										src={job.SupervisorContactPhotoUrl}
										fallbackSrc="avatar"
										alt="user-image"
									/>
									<span className="officer-name truncate">
										<div>
											<strong>{`${job.SupervisorName}`}</strong>
										</div>
										<div>
											<em>{`${job.SupervisorRank}`}</em>
										</div>
									</span>
								</div>
								<div
									className={`col-xs-12 ${type === 30 ? "col-md-2" : "col-md-2"}`}
								>
									<br className="visible-xs visible-sm" />
									<strong className="visible-xs-inline visible-sm-inline">{`${lang.t("Requested Date")}: `}</strong>
									{`${utils.formatDate(`${job.CreateDateUTC}Z`, "en-US", user.TimeZoneId, "numeric")}`}
								</div>
								<div
									className={`col-xs-12 ${type === 30 ? "col-md-2" : "col-md-2"}`}
								>
									<strong className="visible-xs-inline visible-sm-inline">{`${lang.t("Job Start Date")}: `}</strong>
									{utils.formatDate(
										`${job.JobStartDateTime}Z`,
										"en-US",
										user.TimeZoneId,
										user.UserPreferences === "useMilitaryTime"
											? "dt-short-mil"
											: "dt-short",
									)}
								</div>
								<div
									className={`col-xs-12 ${type === 30 ? "col-md-1" : "col-md-1"}`}
								>
									<br className="visible-xs visible-sm" />
									<strong className="visible-xs-inline visible-sm-inline">{`${lang.t("Status")}: `}</strong>
									{`${job.ApprovedTypeDescription}`}
								</div>
								<div
									className={`col-xs-12 ${type === 30 ? "col-md-1" : "col-md-1"}`}
								>
									<br className="visible-xs visible-sm" />
									<strong className="visible-xs-inline visible-sm-inline">{`${lang.t("Date")}: `}</strong>
									{`${utils.formatDate(`${job.ApprovalOrDenialDate}Z`, "en-US", user.TimeZoneId, "numeric")}`}
								</div>
								<If condition={type === 30}>
									<div className="col-xs-12 col-md-2 col-lg-2 button-container approval-action__buttons">
										<button
											type="button"
											className="btn btn-danger"
											onClick={e => {
												e.preventDefault();
												this.denyJob(job, false);
											}}
										>
											{lang.t("Deny")}
										</button>
										<button
											type="button"
											className="btn btn-primary"
											onClick={e => {
												e.preventDefault();
												this.approveJob(job, true);
											}}
										>
											{lang.t("Approve")}
										</button>
									</div>
								</If>
							</div>
						</Link>
					</For>
					<If condition={hasMore}>
						<ScrollDetect
							className="text-center"
							onVisible={() => this.getJobApprovals()}
						>
							<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
						</ScrollDetect>
					</If>
				</div>
				{/* Job Approval List */}

				{/* Bulk Job Actions */}
				<div className={`bulk-actions ${selectedJobs.length > 0 ? "show-actions" : ""}`}>
					<div className="row">
						<div className="col-xs-12 text-center">
							<button
								type="button"
								className="btn btn-default"
								onClick={() => {
									this.updateJobs(true);
								}}
							>
								<span className="text-primary">{lang.t("Approve Selected")}</span>
							</button>
							<button
								type="button"
								className="btn btn-default"
								onClick={() => {
									this.updateJobs(false);
								}}
							>
								<span className="text-danger">{lang.t("Deny Selected")}</span>
							</button>
						</div>
					</div>
				</div>
				{/* Bulk Job Actions */}
			</div>
		);
	}
}

JobApprovalList.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object,
};

JobApprovalList.defaultProps = {
	user: {},
	router: {},
};

export default JobApprovalList;
